import isBoolean from 'lodash/fp/isBoolean';
import isEmpty from 'lodash/fp/isEmpty';

import { GOOGLE_ADDRESS_VALIDATION_FIELD_CONFIRMED } from 'Apps/Checkout/constants/checkoutAddressValidationStatus';
import { CHECKOUT_NEW_ADDRESS_FIELD_VALUE } from 'Apps/Checkout/constants/checkoutInputValues';

import * as AddressService from 'Services/AddressService';
import * as UserService from 'Services/UserService';

import {
  ADDRESS_VALIDATION_FIELD_ADDRESS_SIMPLIFIED,
  googleAddressValidationApiFields,
  googleAddressValidationApiSimplifiedAddressFields,
} from 'constants/addresses';

import {
  trackAddShippingInfo,
  trackCartUpdated,
  trackCreateAddress,
  trackUpdateAddress,
} from 'dux/tracking/actions';
import { patchUser } from 'dux/user/actions';
import { getNewAddress } from 'dux/checkoutAddresses/selectors';
import { getTextMarketingOptin, getTextOptin, getUser, getUserPhone } from 'dux/user/selectors';
import { patchCartV2 } from 'dux/cartV2/thunks';
// Relies only on cart actions
import {
  ADDRESS_VALIDATION_CHOICE,
  ADDRESS_VALIDATION_INVALID_FIELDS,
  LOAD_ADDRESSES_FAILURE,
  LOAD_ADDRESSES_REQUEST,
  LOAD_ADDRESSES_SUCCESS,
  SET_NEW_ADDRESS,
} from 'dux/checkoutAddresses/actionTypes';
import { updateCart } from 'dux/checkoutCart/thunks';

/**
 * FETCH
 */

export const loadAddresses = () => async dispatch => {
  dispatch({ type: LOAD_ADDRESSES_REQUEST });
  try {
    const data = await UserService.fetchAddresses();
    dispatch({ type: LOAD_ADDRESSES_SUCCESS, data });
    dispatch(trackUpdateAddress(data));
  } catch (error) {
    dispatch({ type: LOAD_ADDRESSES_FAILURE, error });
  }
};

/**
 * PATCH
 */

export const saveContactAddress =
  ({ values }) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const phone = getUserPhone(state);
      const textMarketingOptin = getTextMarketingOptin(state);
      const textOptin = getTextOptin(state);

      // Save shipping address if needed
      if (values.addressPubkey !== CHECKOUT_NEW_ADDRESS_FIELD_VALUE) {
        dispatch(updateCart({ shipping_address: values.addressPubkey }));
      }

      if (values.addressPubkey === CHECKOUT_NEW_ADDRESS_FIELD_VALUE) {
        /**
         * Recovers address from store to recover an eventual pubkey
         * this avoids creating multiple addresses by recursively saving the new address
         */
        const addressToPersist = {
          ...getNewAddress(state),
          ...values.newAddress,
        };
        const address = await UserService.persistAddress(addressToPersist);
        dispatch({
          type: SET_NEW_ADDRESS,
          data: address,
        });
        dispatch(trackCreateAddress(address));
        dispatch(updateCart({ shipping_address: address.pubkey }));
        dispatch(trackAddShippingInfo());
        dispatch(trackCartUpdated());
      }

      const userParams = {};

      if (phone !== values.phone) {
        userParams.phone = values.phone;
      }

      if (isBoolean(values.textOptin) && textOptin !== values.textOptin) {
        userParams.text_optin = values.textOptin;
      }

      if (
        isBoolean(values.textMarketingOptin) &&
        textMarketingOptin !== values.textMarketingOptin
      ) {
        userParams.text_marketing_optin = values.textMarketingOptin;
      }

      // Update user is needed
      if (!isEmpty(userParams)) {
        await dispatch(patchUser(userParams));
      }

      return { status: 'success' };
    } catch (error) {
      return { status: 'failure', addressError: error };
    }
  };

/**
 * PATCH
 */

export const saveAccountDetails =
  ({ values }) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const phone = getUserPhone(state);
      const user = getUser(state);
      const textMarketingOptin = getTextMarketingOptin(state);
      const textOptin = getTextOptin(state);

      const userParams = {};

      if (user.first_name !== values.account.firstName) {
        userParams.first_name = values.account.firstName;
      }
      if (user.last_name !== values.account.lastName) {
        userParams.last_name = values.account.lastName;
      }

      if (user.email !== values.account.email) {
        userParams.email = values.account.email;
        userParams.username = values.account.email;
      }

      if (phone !== values.account.phone) {
        userParams.phone = values.account.phone;
      }

      if (isBoolean(values.textOptin) && textOptin !== values.textOptin) {
        userParams.text_optin = values.textOptin;
      }

      if (
        isBoolean(values.textMarketingOptin) &&
        textMarketingOptin !== values.textMarketingOptin
      ) {
        userParams.text_marketing_optin = values.textMarketingOptin;
      }

      // Update user is needed
      if (!isEmpty(userParams)) {
        await dispatch(patchUser(userParams));
      }

      return { status: 'success' };
    } catch (error) {
      return { status: 'failure', userError: error };
    }
  };

/**
 * PATCH
 */

export const saveShippingAddress =
  ({ values }) =>
  async (dispatch, getState) => {
    try {
      const state = getState();

      // Save shipping address if needed
      if (values.addressPubkey !== CHECKOUT_NEW_ADDRESS_FIELD_VALUE) {
        dispatch(updateCart({ shipping_address: values.addressPubkey }));
      }

      if (values.addressPubkey === CHECKOUT_NEW_ADDRESS_FIELD_VALUE) {
        /**
         * Recovers address from store to recover an eventual pubkey
         * this avoids creating multiple addresses by recursively saving the new address
         */
        const addressToPersist = {
          ...getNewAddress(state),
          ...values.newAddress,
        };
        const address = await UserService.persistAddress(addressToPersist);
        dispatch({
          type: SET_NEW_ADDRESS,
          data: address,
        });
        dispatch(trackCreateAddress(address));
        dispatch(updateCart({ shipping_address: address.pubkey }));
        dispatch(trackAddShippingInfo());
        dispatch(trackCartUpdated());
      }

      return { status: 'success' };
    } catch (error) {
      return { status: 'failure', addressError: error };
    }
  };

export const saveShippingAddressV2 =
  ({ values }) =>
  async (dispatch, getState) => {
    try {
      const state = getState();

      // Save shipping address if needed
      if (values.addressPubkey !== CHECKOUT_NEW_ADDRESS_FIELD_VALUE) {
        dispatch(patchCartV2({ shipping_address: values.addressPubkey }));
      }

      if (values.addressPubkey === CHECKOUT_NEW_ADDRESS_FIELD_VALUE) {
        /**
         * Recovers address from store to recover an eventual pubkey
         * this avoids creating multiple addresses by recursively saving the new address
         */
        const addressToPersist = {
          ...getNewAddress(state),
          ...values.newAddress,
        };
        const address = await UserService.persistAddress(addressToPersist);
        dispatch({
          type: SET_NEW_ADDRESS,
          data: address,
        });
        dispatch(trackCreateAddress(address));
        dispatch(patchCartV2({ shipping_address: address.pubkey }));
      }

      return { status: 'success' };
    } catch (error) {
      return { status: 'failure', addressError: error };
    }
  };

export const validateAddress =
  ({ values }) =>
  async dispatch => {
    try {
      // Run address validation via cloud function
      const addressValidation = await AddressService.validateAddress({
        regionCode: values.newAddress.country,
        locality: values.newAddress.city,
        administrativeArea: values.newAddress.state,
        postalCode: values.newAddress.zipcode,
        addressLines: [values.newAddress.address1],
      });

      /**
       * To fine-tune the address invalidity, rework conditions here
       * https://developers.google.com/maps/documentation/address-validation/understand-response
       */
      const addressPotentiallyInvalid = addressValidation?.verdict?.hasUnconfirmedComponents;

      // For UX purpose, we merge several fields into a simplified one 'Address not found'
      const addressNotFoundSimplifiedFieldValues = [];

      if (addressPotentiallyInvalid) {
        const fields = addressValidation?.address?.addressComponents
          ?.map(el => {
            if (
              el?.confirmationLevel !== GOOGLE_ADDRESS_VALIDATION_FIELD_CONFIRMED &&
              googleAddressValidationApiSimplifiedAddressFields.includes(el?.componentType)
            ) {
              addressNotFoundSimplifiedFieldValues.push(el?.componentName?.text);
              return null;
            }
            if (el?.confirmationLevel !== GOOGLE_ADDRESS_VALIDATION_FIELD_CONFIRMED) {
              return {
                fieldName: googleAddressValidationApiFields[el?.componentType],
                fieldValue: el?.componentName?.text,
              };
            }
            return null;
          })
          .filter(el => el);

        // For UX purpose, we merge several fields into a simplified one 'Address not found'
        if (!isEmpty(addressNotFoundSimplifiedFieldValues)) {
          fields.push({
            fieldName:
              googleAddressValidationApiFields[ADDRESS_VALIDATION_FIELD_ADDRESS_SIMPLIFIED],
            fieldValue: addressNotFoundSimplifiedFieldValues.join(' '),
          });
        }

        dispatch({ type: ADDRESS_VALIDATION_INVALID_FIELDS, data: fields });
        return { status: 'failure', addressError: 'Address invalid' };
      }
      return { status: 'success' };
    } catch (error) {
      return { status: 'failure', addressError: error };
    }
  };

export const validateAddressChoice = data => async dispatch =>
  dispatch({ type: ADDRESS_VALIDATION_CHOICE, data });

export const resetAddressInvalidFields = () => async dispatch =>
  dispatch({ type: ADDRESS_VALIDATION_INVALID_FIELDS, data: null });

export const resetAddressChoice = () => async dispatch =>
  dispatch({ type: ADDRESS_VALIDATION_CHOICE, data: null });
